import { useUser } from "@clerk/react";
import {
  CompanyDTO,
  HubSpotSaveToListSyncedRecordDTO,
  ListingDTO,
} from "@shaarp/common";
import { SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { KeyContactsDialog } from "./data-table-cell-company-key-contacts";
import { DataTableCompaniesResponsive } from "./data-table-companies-responsive";
import { DataTableToolbarCompaniesExport } from "./data-table-toolbar-companies-export";
import { CompaniesListingAction } from "@/components/company/companies-listing-action";

export default function DataTableCompanies({
  companies,
  selected,
  isLoading = false,
  pagination = true,
  additionalColumns = [],
  additionalFilters = <></>,
  overrideColumnsVisibility = [],
  enabledListingFeature = true,
  sortingState = [{ id: "data-score", desc: true }],
  emptyText = null,
  columnsPersistenceKey = "data-table-companies-v4",
  onPaginationChange = () => {},
  onSortedDataChange,
  searchQuery,
  showAssignedColumn = true,
}: {
  companies: CompanyDTO[];
  selected?: CompanyDTO;
  isLoading?: boolean;
  pagination?: boolean;
  additionalColumns?: any[];
  additionalFilters?: React.ReactNode;
  overrideColumnsVisibility?: string[];
  enabledListingFeature?: boolean;
  sortingState?: SortingState;
  emptyText?: string | null;
  columnsPersistenceKey?: string;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  onSortedDataChange?: (sortedCompanies: CompanyDTO[]) => void;
  searchQuery?: string;
  showAssignedColumn?: boolean;
}) {
  const { user } = useUser();

  const [data, setData] = useState<CompanyDTO[]>([]);

  useEffect(() => {
    setData(companies);
  }, [companies]);

  const rowClassNames = (company: CompanyDTO) => {
    if (selected && selected.id === company.id) {
      return "bg-blue-100 hover:bg-blue-200 dark:bg-blue-950 dark:bg-opacity-50 hover:dark:bg-opacity-75";
    } else if (company.assignation?.userId === user?.id) {
      return "bg-green-100 hover:bg-green-200 dark:bg-green-950 dark:bg-opacity-50 hover:dark:bg-opacity-75";
    }
    return "";
  };

  const onCompaniesSynced = (syncedRecords: HubSpotSaveToListSyncedRecordDTO[]) => {
    const syncMap = new Map(syncedRecords.map((r) => [r.id, r]));
    setData((prev) =>
      prev.map((company) => {
        const synced = syncMap.get(company.id);
        if (!synced) return company;
        return {
          ...company,
          integrations: {
            ...company.integrations,
            hubspot: {
              isSynced: true,
              externalId: synced.externalId,
              directLink: synced.directLink ?? company.integrations?.hubspot?.directLink ?? null,
            },
          },
        };
      })
    );
  };

  /** Keeps the rows' list badges in sync once the companies are attached server-side. */
  const onCompaniesAttached = (listing: ListingDTO) => {
    data.forEach((company) => {
      company.listingsIds?.push(listing.id);
    });
  };

  const onRowChanged = (row: CompanyDTO) => {
    setData(
      companies.map((c) => {
        if (c.id === row.id) {
          return row;
        }
        return c;
      })
    );
  };

  return (
    <>
      <KeyContactsDialog />
      <DataTableCompaniesResponsive
        companies={data}
        selected={selected}
        isLoading={isLoading}
        additionalColumns={additionalColumns}
        overrideColumnsVisibility={overrideColumnsVisibility}
        onRowChanged={onRowChanged}
        pagination={pagination}
        rowClassNames={rowClassNames}
        enabledListingFeature={enabledListingFeature}
        sortingState={sortingState}
        additionalFilters={additionalFilters}
        onPaginationChange={onPaginationChange}
        onSortedDataChange={onSortedDataChange}
        columnsPersistenceKey={columnsPersistenceKey}
        emptyText={emptyText}
        showAssignedColumn={showAssignedColumn}
        toolbarContent={
          <>
            {enabledListingFeature && (
              <CompaniesListingAction
                companiesIds={data.map((company) => company.id)}
                disabled={isLoading || data.length === 0}
                searchQuery={searchQuery}
                triggerClassName="hidden ml-auto h-8 lg:flex"
                onAttached={onCompaniesAttached}
              />
            )}
            <DataTableToolbarCompaniesExport
              companies={data}
              fetchDataLoading={isLoading}
              onCompaniesSynced={onCompaniesSynced}
            />
          </>
        }
      />
    </>
  );
}
